
/**
 * Permission Manager - Handles role-based access control
 */
import { User } from "@supabase/supabase-js";

export type UserRole = 'admin' | 'user' | 'moderator' | 'editor';

export interface PermissionState {
  role: UserRole;
  permissions: Set<string>;
  isAdmin: boolean;
  version: number; // Add version tracking for state changes
}

// Define sync state for permission syncing
export enum SyncState {
  IDLE = 'idle',
  SYNCING = 'syncing',
  SYNCED = 'synced',
  ERROR = 'error'
}

// Cache TTL in milliseconds - 5 minutes
const ROLE_CACHE_TTL = 5 * 60 * 1000;

export class PermissionManager {
  private static instance: PermissionManager;
  private state: PermissionState = {
    role: 'user',
    permissions: new Set(),
    isAdmin: false,
    version: 0
  };
  private listeners: Set<(state: PermissionState, syncState: SyncState) => void> = new Set();
  private syncState: SyncState = SyncState.IDLE;
  private retryCount: number = 0;
  private maxRetries: number = 3;
  private retryTimeout: NodeJS.Timeout | null = null;
  private permissionCache: Map<string, boolean> = new Map();
  private lastRoleUpdate: number = 0;
  private roleFetchInProgress: boolean = false;
  private userIdCache: string | null = null;

  private constructor() {}

  public static getInstance(): PermissionManager {
    if (!PermissionManager.instance) {
      PermissionManager.instance = new PermissionManager();
    }
    return PermissionManager.instance;
  }

  /**
   * Get current state
   */
  public getState(): PermissionState {
    return { ...this.state };
  }

  /**
   * Get current sync state
   */
  public getSyncState(): SyncState {
    return this.syncState;
  }

  /**
   * Get current state version
   */
  public getStateVersion(): number {
    return this.state.version;
  }

  /**
   * Check if role update is needed based on time and user ID
   */
  private isRoleUpdateNeeded(userId: string): boolean {
    const now = Date.now();
    // Update if:
    // 1. User ID has changed
    // 2. We haven't updated in a while
    // 3. We're in an error state
    return (
      this.userIdCache !== userId ||
      now - this.lastRoleUpdate > ROLE_CACHE_TTL ||
      this.syncState === SyncState.ERROR ||
      this.syncState === SyncState.IDLE
    );
  }

  /**
   * Update user role and permissions with version tracking
   */
  public updateUserRole(role: UserRole | null | undefined, userId?: string): boolean {
    console.log(`PermissionManager: Updating role to ${role} for user ${userId || 'current'}`);
    
    // Default to user if role is undefined or null
    const newRole = role || 'user';
    
    // Store userId for cache invalidation
    if (userId) {
      this.userIdCache = userId;
    }
    
    // Update timestamp
    this.lastRoleUpdate = Date.now();
    
    // Increment version to track state changes
    const newVersion = this.state.version + 1;
    
    // Set state with new version
    this.state = {
      role: newRole,
      permissions: this.getPermissionsForRole(newRole),
      isAdmin: newRole === 'admin',
      version: newVersion
    };

    // Clear permission cache when role changes
    this.clearPermissionCache();

    // Update sync state
    this.syncState = SyncState.SYNCED;
    this.retryCount = 0;
    this.roleFetchInProgress = false;
    
    // Cancel any pending retries
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    // Notify listeners
    this.notifyListeners();
    return true;
  }

  /**
   * Attempt to update user role with retry logic
   */
  public async updateUserRoleWithRetry(role: UserRole | null | undefined, userId?: string): Promise<boolean> {
    // If we're already fetching a role, don't start another fetch
    if (this.roleFetchInProgress) {
      console.log('Role fetch already in progress, skipping');
      return true;
    }
    
    try {
      // If userId is provided and role update is not needed, skip update
      if (userId && !this.isRoleUpdateNeeded(userId)) {
        console.log('Role update not needed, using cached role');
        return true;
      }
      
      this.roleFetchInProgress = true;
      this.syncState = SyncState.SYNCING;
      this.notifyListeners(); // Notify of sync starting
      
      const success = this.updateUserRole(role, userId);
      
      if (success) {
        return true;
      } else {
        return this.scheduleRetry(role, userId);
      }
    } catch (error) {
      console.error("Error updating user role:", error);
      return this.scheduleRetry(role, userId);
    }
  }

  /**
   * Schedule a retry for role update with exponential backoff
   */
  private scheduleRetry(role: UserRole | null | undefined, userId?: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.retryCount >= this.maxRetries) {
        this.syncState = SyncState.ERROR;
        this.roleFetchInProgress = false;
        console.error(`Failed to update role to ${role} after ${this.maxRetries} attempts`);
        this.notifyListeners(); // Notify listeners of error state
        resolve(false);
        return;
      }

      // Exponential backoff with jitter
      const delay = Math.min(1000 * (2 ** this.retryCount) + Math.random() * 100, 10000);
      this.retryCount++;
      
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
      }
      
      this.retryTimeout = setTimeout(async () => {
        const success = this.updateUserRole(role, userId);
        resolve(success);
      }, delay);
    });
  }

  /**
   * Check if user has a specific role
   */
  public hasRole(role: UserRole): boolean {
    return this.state.role === role;
  }

  /**
   * Check if user is an admin
   */
  public isAdmin(): boolean {
    return this.state.isAdmin;
  }

  /**
   * Check if user has a specific permission with caching
   */
  public hasPermission(permission: string): boolean {
    // Check cache first
    if (this.permissionCache.has(permission)) {
      return this.permissionCache.get(permission) || false;
    }
    
    // Compute and cache result
    const result = this.state.permissions.has(permission);
    this.permissionCache.set(permission, result);
    return result;
  }

  /**
   * Check if user has any of the specified permissions with optimization
   */
  public hasAnyPermission(permissions: string[]): boolean {
    // Optimize for common cases
    if (permissions.length === 0) return false;
    if (this.state.isAdmin) return true;
    
    // Check cache first for all permissions
    const allCached = permissions.every(p => this.permissionCache.has(p));
    if (allCached) {
      return permissions.some(p => this.permissionCache.get(p));
    }
    
    // Compute and cache results
    return permissions.some(permission => {
      const result = this.hasPermission(permission);
      this.permissionCache.set(permission, result);
      return result;
    });
  }

  /**
   * Check if user has all of the specified permissions with optimization
   */
  public hasAllPermissions(permissions: string[]): boolean {
    // Optimize for common cases
    if (permissions.length === 0) return true;
    if (this.state.isAdmin) return true;
    
    // Check cache first for all permissions
    const allCached = permissions.every(p => this.permissionCache.has(p));
    if (allCached) {
      return permissions.every(p => this.permissionCache.get(p));
    }
    
    // Compute and cache results
    return permissions.every(permission => {
      const result = this.hasPermission(permission);
      this.permissionCache.set(permission, result);
      return result;
    });
  }

  /**
   * Get all permissions for the current user
   */
  public getPermissions(): Set<string> {
    return new Set(this.state.permissions);
  }

  /**
   * Get permissions for a specific role with caching
   */
  private getPermissionsForRole(role: UserRole): Set<string> {
    const permissions = new Set<string>();
    
    // Add base permissions for all users
    permissions.add('view:dashboard');
    permissions.add('view:profile');
    
    // Add role-specific permissions
    if (role === 'admin') {
      // Admin permissions (full access)
      permissions.add('view:users');
      permissions.add('manage:users');
      permissions.add('view:blog');
      permissions.add('manage:blog');
      permissions.add('view:testimonials');
      permissions.add('manage:testimonials');
      permissions.add('view:settings');
      permissions.add('manage:settings');
      permissions.add('manage:newsletters');
      permissions.add('view:linkedin');      // Add LinkedIn permissions
      permissions.add('manage:linkedin');    // Add LinkedIn permissions
    } else if (role === 'moderator') {
      // Moderator permissions
      permissions.add('view:testimonials');
      permissions.add('manage:testimonials');
      permissions.add('view:blog');
    } else if (role === 'editor') {
      // Editor permissions
      permissions.add('view:blog');
      permissions.add('manage:blog');
      permissions.add('manage:newsletters');
      permissions.add('view:linkedin');      // Add LinkedIn permissions
      permissions.add('manage:linkedin');    // Add LinkedIn permissions
    }
    
    return permissions;
  }

  /**
   * Subscribe to permission changes with state version
   */
  public subscribe(callback: (state: PermissionState, syncState: SyncState) => void): () => void {
    this.listeners.add(callback);
    
    // Immediately call with current state
    callback(this.state, this.syncState);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Refresh user permissions from the current state
   * This can be used to reload permissions after a role change
   */
  public refreshPermissions(): void {
    // Only refresh if we're not already syncing
    if (this.syncState !== SyncState.SYNCING) {
      // Re-apply the current role to refresh all permissions
      this.updateUserRole(this.state.role);
    }
  }

  /**
   * Clear all permissions (used during sign out)
   */
  public clearPermissions() {
    console.log('PermissionManager: Clearing all permissions and role state');
    
    // Store the previous state for logging
    const previousRole = this.state.role;
    const previousPermissionsCount = this.state.permissions.size;
    
    // Reset to default state
    this.state = {
      role: 'user',
      permissions: new Set(),
      isAdmin: false,
      version: this.state.version + 1
    };
    
    // Clear permission cache
    this.clearPermissionCache();
    this.userIdCache = null;
    this.lastRoleUpdate = 0;
    
    // Reset sync state
    this.syncState = SyncState.IDLE;
    this.roleFetchInProgress = false;
    
    // Cancel any pending retries
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    // Log the cleanup details
    console.log(`PermissionManager: Cleared ${previousPermissionsCount} permissions and reset role from '${previousRole}' to 'user'`);
    
    // Notify listeners of state change
    this.notifyListeners();
  }

  /**
   * Clear permission cache
   */
  private clearPermissionCache() {
    this.permissionCache.clear();
  }

  /**
   * Grant a specific permission to the current user
   * This can be used for temporary or dynamic permissions
   */
  public grantPermission(permission: string): void {
    this.state.permissions.add(permission);
    this.permissionCache.set(permission, true);
    this.state.version += 1;
    this.notifyListeners();
  }

  /**
   * Revoke a specific permission from the current user
   */
  public revokePermission(permission: string): void {
    this.state.permissions.delete(permission);
    this.permissionCache.set(permission, false);
    this.state.version += 1;
    this.notifyListeners();
  }

  /**
   * Grant multiple permissions to the current user
   */
  public grantPermissions(permissions: string[]): void {
    let hasChanges = false;
    
    permissions.forEach(permission => {
      if (!this.state.permissions.has(permission)) {
        this.state.permissions.add(permission);
        this.permissionCache.set(permission, true);
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      this.state.version += 1;
      this.notifyListeners();
    }
  }

  /**
   * Revoke multiple permissions from the current user
   */
  public revokePermissions(permissions: string[]): void {
    let hasChanges = false;
    
    permissions.forEach(permission => {
      if (this.state.permissions.has(permission)) {
        this.state.permissions.delete(permission);
        this.permissionCache.set(permission, false);
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      this.state.version += 1;
      this.notifyListeners();
    }
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners() {
    this.listeners.forEach(listener => {
      listener(this.state, this.syncState);
    });
  }
}

// Export a singleton instance
export const permissionManager = PermissionManager.getInstance();
